import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './LeaveCalendarWidget.css';
import publicHolidays from '../data/public_holidays_2025_2050.json';

const LeaveCalendarWidget = ({ 
  leaveRequests = [], 
  onDateClick, 
  onRequestClick, 
  onRequestLeave,
  hasTeam = false,
  teamLeaveRequests = [],
  currentView = 'my',
  onViewChange,
  departments = [],
  selectedDepartment = null,
  onDepartmentChange
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState(null);
  const [tooltipDate, setTooltipDate] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const tooltipTimeoutRef = useRef(null);
  
  // Determine which leave requests to display based on current view
  const displayLeaveRequests = currentView === 'team' ? teamLeaveRequests : leaveRequests;

  // Get current month and year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Month names
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Abbreviated month names for date ranges
  const monthNamesShort = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Day names
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const firstDayOfWeek = (firstDayOfMonth.getDay() + 6) % 7; // Monday=0, Sunday=6
    const daysInMonth = lastDayOfMonth.getDate();

    const calendarDays = Array(firstDayOfWeek).fill(null);

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(day);
    }

    return calendarDays;
  };

  // Check if a date is today
  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth === today.getMonth() &&
      currentYear === today.getFullYear()
    );
  };

  // Get public holidays for a specific date
  const getHolidaysForDate = (day) => {
    if (!day) return [];
    
    // Format date as YYYY-MM-DD without timezone conversion
    const year = currentYear;
    const month = String(currentMonth + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateString = `${year}-${month}-${dayStr}`;
    
    return publicHolidays.filter(holiday => holiday.date === dateString);
  };

  // Get leave requests for a specific date (excluding cancelled and rejected)
  const getLeaveRequestsForDate = (day) => {
    if (!day) return [];
    
    const targetDate = new Date(currentYear, currentMonth, day);
    targetDate.setHours(0, 0, 0, 0);
    
    return displayLeaveRequests.filter(request => {
      const startDate = new Date(request.start_date);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(request.end_date);
      endDate.setHours(0, 0, 0, 0);
      
      // Exclude cancelled and rejected leave requests
      return targetDate >= startDate && targetDate <= endDate && request.status !== 'cancelled' && request.status !== 'rejected';
    });
  };

  // Get the primary status for a date (highest priority status)
  const getPrimaryStatusForDate = (day) => {
    const requests = getLeaveRequestsForDate(day);
    if (requests.length === 0) return null;

    // Priority order: approved > pending > completed > rejected > cancelled
    const statusPriority = {
      'approved': 5,
      'pending': 4,
      'completed': 3,
      'rejected': 2,
      'cancelled': 1
    };

    return requests.reduce((highest, request) => {
      const currentPriority = statusPriority[request.status] || 0;
      const highestPriority = statusPriority[highest?.status] || 0;
      return currentPriority > highestPriority ? request : highest;
    }, null);
  };

  // Get CSS class for a calendar day
  const getDayClass = (day) => {
    if (!day) return 'leave-calendar-day empty';
    
    const requests = getLeaveRequestsForDate(day);
    const holidays = getHolidaysForDate(day);
    let classes = 'leave-calendar-day';
    
    if (isToday(day)) {
      classes += ' today';
    }
    
    if (requests.length > 0) {
      classes += ' has-leave';
    }
    
    if (holidays.length > 0) {
      classes += ' is-holiday';
    }
    
    if (hoveredDate === day) {
      classes += ' hovered';
    }
    
    return classes;
  };

  // Handle day click
  const handleDayClick = (day) => {
    if (!day) return;
    
    const requests = getLeaveRequestsForDate(day);
    const date = new Date(currentYear, currentMonth, day);
    
    if (requests.length > 0) {
      // If there are leave requests, call the request click handler
      if (onRequestClick) {
        onRequestClick(requests, date);
      }
    } else {
      // If no leave requests, call the date click handler
      if (onDateClick) {
        onDateClick(date);
      }
    }
  };

  // Handle day hover
  const handleDayHover = (day, event) => {
    // Cancel any pending hide timeout when hovering over a new cell
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    
    setHoveredDate(day);
    if (day && (getLeaveRequestsForDate(day).length > 0 || getHolidaysForDate(day).length > 0)) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
      setTooltipDate(day);
    }
  };

  // Handle day leave - start timeout to hide tooltip, but allow cursor to reach it
  const handleDayLeave = () => {
    setHoveredDate(null);
    
    // Set a short timeout before hiding tooltip
    // This gives user time to move cursor onto tooltip
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltipDate(null);
    }, 200); // 200ms delay
  };

  // Handle tooltip hover - cancel hide timeout and keep tooltip visible
  const handleTooltipEnter = () => {
    // Clear the timeout if cursor reaches tooltip in time
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  };

  // Handle tooltip leave - hide tooltip immediately when cursor leaves tooltip area
  const handleTooltipLeave = () => {
    setTooltipDate(null);
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  // Format date range for tooltip
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const startMonth = monthNamesShort[start.getMonth()];
    const startDay = start.getDate();
    const endMonth = monthNamesShort[end.getMonth()];
    const endDay = end.getDate();
    
    // If same month, show "Oct 13 - 23"
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${startMonth} ${startDay} - ${endDay}`;
    }
    
    // If different months, show "Oct 13 - Nov 5"
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  };

  // Get tooltip content for a day
  const getTooltipContent = (day) => {
    if (!day) return null;
    
    const requests = getLeaveRequestsForDate(day);
    if (requests.length === 0) return null;
    
    return requests.map(request => {
      // Use gray color for completed leave (vastly different from all leave type colors)
      const completedColor = '#4b5563'; // Gray-600 from COLOR_REFERENCE.md
      const displayColor = request.status === 'completed' 
        ? completedColor 
        : (request.leave_type_details?.color || '#4CAF50');
      
      return {
        employeeName: currentView === 'team' ? request.employee_name : null,
        type: request.leave_type_details?.name || 'Absent',
        status: request.status,
        dateRange: formatDateRange(request.start_date, request.end_date),
        color: displayColor,
        days: request.total_days,
        reason: request.reason
      };
    });
  };

  // Calculate leave spans for line rendering
  const calculateLeaveSpans = () => {
    const calendarDays = generateCalendarDays();
    const spans = [];
    
    // Get first and last day of current month view
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    firstDayOfMonth.setHours(0, 0, 0, 0);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    lastDayOfMonth.setHours(0, 0, 0, 0);
    
    // Process each leave request
    displayLeaveRequests.forEach(request => {
      // Skip cancelled and rejected requests
      if (request.status === 'cancelled' || request.status === 'rejected') {
        return;
      }
      
      const startDate = new Date(request.start_date);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(request.end_date);
      endDate.setHours(0, 0, 0, 0);
      
      // Check if leave extends beyond current month boundaries
      const startsBeforeMonth = startDate < firstDayOfMonth;
      const endsAfterMonth = endDate > lastDayOfMonth;
      
      // Find which calendar cells this leave occupies
      const occupiedCells = [];
      calendarDays.forEach((day, index) => {
        if (!day) return;
        
        const cellDate = new Date(currentYear, currentMonth, day);
        cellDate.setHours(0, 0, 0, 0);
        
        if (cellDate >= startDate && cellDate <= endDate) {
          occupiedCells.push({ index, day, row: Math.floor(index / 7) });
        }
      });
      
      if (occupiedCells.length === 0) return;
      
      // Break into row segments
      const rowSegments = [];
      let currentSegment = [occupiedCells[0]];
      
      for (let i = 1; i < occupiedCells.length; i++) {
        if (occupiedCells[i].row === currentSegment[currentSegment.length - 1].row) {
          currentSegment.push(occupiedCells[i]);
        } else {
          rowSegments.push(currentSegment);
          currentSegment = [occupiedCells[i]];
        }
      }
      rowSegments.push(currentSegment);
      
      // Create span objects for each segment
      rowSegments.forEach((segment, segmentIndex) => {
        const startCell = segment[0];
        const endCell = segment[segment.length - 1];
        const startCol = startCell.index % 7;
        const endCol = endCell.index % 7;
        
        // Check if this is the first visible segment (leave may have started before month)
        const isFirstVisibleSegment = segmentIndex === 0;
        // Check if this is the last visible segment (leave may continue after month)
        const isLastVisibleSegment = segmentIndex === rowSegments.length - 1;
        
        // Use gray color for completed leave
        const completedColor = '#4b5563';
        const displayColor = request.status === 'completed' 
          ? completedColor 
          : (request.leave_type_details?.color || '#4CAF50');
        
        spans.push({
          row: startCell.row,
          startCol,
          endCol,
          startDay: startCell.day,
          endDay: endCell.day,
          color: displayColor,
          request,
          startDate,
          endDate,
          // Flags for month boundary handling
          extendsFromPreviousMonth: isFirstVisibleSegment && startsBeforeMonth,
          extendsToNextMonth: isLastVisibleSegment && endsAfterMonth
        });
      });
    });
    
    // Assign stack levels for overlapping spans
    spans.forEach((span, index) => {
      let stackLevel = 0;
      
      // Check all previous spans in the same row
      for (let i = 0; i < index; i++) {
        const other = spans[i];
        
        // If same row and overlapping columns
        if (other.row === span.row) {
          const spanStart = span.startCol;
          const spanEnd = span.endCol;
          const otherStart = other.startCol;
          const otherEnd = other.endCol;
          
          // Check if they overlap
          if (!(spanEnd < otherStart || spanStart > otherEnd)) {
            stackLevel = Math.max(stackLevel, (other.stackLevel || 0) + 1);
          }
        }
      }
      
      span.stackLevel = stackLevel;
    });
    
    // Initialize connection flags on all spans
    spans.forEach(span => {
      span.connectsToNext = false;
      span.connectsToPrevious = false;
    });
    
    // Sort spans by start date for consecutive leave detection (but keep reference to original objects)
    const sortedSpans = [...spans].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    
    // Detect consecutive leaves of the same type and mark them as connected
    sortedSpans.forEach((span, index) => {
      // Check if this span connects to the next leave
      for (let i = index + 1; i < sortedSpans.length; i++) {
        const nextSpan = sortedSpans[i];
        
        // Check if leaves are consecutive (current end date + 1 day = next start date)
        const nextDay = new Date(span.endDate);
        nextDay.setDate(nextDay.getDate() + 1);
        nextDay.setHours(0, 0, 0, 0);
        
        const nextStartDate = new Date(nextSpan.startDate);
        nextStartDate.setHours(0, 0, 0, 0);
        
        // Check if:
        // 1. Dates are consecutive
        // 2. Same leave type ID
        // 3. Same employee (for team view)
        const areDatesConsecutive = nextDay.getTime() === nextStartDate.getTime();
        const sameLeaveType = span.request.leave_type === nextSpan.request.leave_type;
        const sameEmployee = span.request.employee === nextSpan.request.employee;
        
        if (areDatesConsecutive && sameLeaveType && sameEmployee) {
          span.connectsToNext = true;
          nextSpan.connectsToPrevious = true;
          break;
        }
      }
    });
    
    return spans;
  };

  const calendarDays = generateCalendarDays();
  const leaveSpans = calculateLeaveSpans();
  
  // Calculate maximum stack level per row for dynamic row heights
  const getMaxStackLevelPerRow = () => {
    const totalRows = Math.ceil(calendarDays.length / 7);
    const maxStackPerRow = Array(totalRows).fill(0);
    
    leaveSpans.forEach(span => {
      const currentMax = maxStackPerRow[span.row] || 0;
      maxStackPerRow[span.row] = Math.max(currentMax, span.stackLevel + 1); // +1 because stackLevel is 0-indexed
    });
    
    return maxStackPerRow;
  };
  
  const maxStackPerRow = getMaxStackLevelPerRow();

  return (
    <div className="leave-calendar-widget">
      <div className="leave-calendar-header">
        <div className="calendar-navigation">
          {/* Segmented Control for Supervisor/HOD */}
          {hasTeam && onViewChange && (
            <div className="calendar-view-toggle">
              <button 
                type="button"
                className={`toggle-option ${currentView === 'my' ? 'active' : ''}`}
                onClick={() => onViewChange('my')}
              >
                My Leave
              </button>
              <button 
                type="button"
                className={`toggle-option ${currentView === 'team' ? 'active' : ''}`}
                onClick={() => onViewChange('team')}
              >
                Team Leave
              </button>
            </div>
          )}
          
          <button 
            type="button" 
            className="nav-button prev" 
            onClick={goToPreviousMonth}
            aria-label="Previous month"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15,18 9,12 15,6"></polyline>
            </svg>
          </button>
          
          <div className="calendar-title">
            <h3>{monthNames[currentMonth]} {currentYear}</h3>
          </div>
          
          <button 
            type="button" 
            className="nav-button next" 
            onClick={goToNextMonth}
            aria-label="Next month"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9,6 15,12 9,18"></polyline>
            </svg>
          </button>
          
          <button 
            type="button" 
            className="today-button-nav" 
            onClick={goToToday}
          >
            Today
          </button>
        </div>
        
        <button 
          type="button" 
          className="request-leave-button" 
          onClick={onRequestLeave}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Request Leave
        </button>
      </div>

      {/* Department filter - shown in team view when departments exist */}
      {currentView === 'team' && departments.length > 1 && onDepartmentChange && (
        <div className="department-filter">
          <button
            type="button"
            className={`dept-filter-option ${selectedDepartment === null ? 'active' : ''}`}
            onClick={() => onDepartmentChange(null)}
          >
            All
          </button>
          {departments.map(dept => (
            <button
              key={dept.id}
              type="button"
              className={`dept-filter-option ${selectedDepartment === dept.id ? 'active' : ''}`}
              onClick={() => onDepartmentChange(dept.id)}
            >
              {dept.name}
            </button>
          ))}
        </div>
      )}

      <div className="leave-calendar-grid">
        {/* Day headers */}
        <div className="calendar-day-headers">
          {dayNames.map((day, index) => (
            <div key={index} className="day-header">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="calendar-days-wrapper">
          <div 
            className="calendar-days"
            style={{
              // Set dynamic row heights based on max stack levels
              gridTemplateRows: maxStackPerRow.map(maxStack => {
                // Base height (60px) + space for stacked lines (11px per stack level)
                const baseHeight = 60;
                const stackHeight = maxStack > 0 ? maxStack * 11 + 10 : 0; // +10px padding at bottom
                return `${baseHeight + stackHeight}px`;
              }).join(' ')
            }}
          >
            {calendarDays.map((day, index) => {
              return (
                <div
                  key={index}
                  className={getDayClass(day)}
                  onClick={() => handleDayClick(day)}
                  onMouseEnter={(e) => handleDayHover(day, e)}
                  onMouseLeave={handleDayLeave}
                >
                  {day && (
                    <>
                      <span className="day-number">{day}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Leave Lines Container - Overlay */}
          <div className="leave-lines-container">
            {leaveSpans.map((span, index) => {
              // Calculate horizontal positioning
              const cellWidth = 100 / 7; // Each cell is 1/7 of the width
              
              // Determine if this segment is ACTUALLY the start of the leave
              // Only center if this is where the leave actually starts AND it doesn't connect to a previous leave
              // Otherwise, start at left edge (continuation from previous month/week OR connecting from previous leave)
              const actualStartDateInCurrentMonth = span.startDate.getMonth() === currentMonth && 
                                                     span.startDate.getFullYear() === currentYear;
              const isActualStart = actualStartDateInCurrentMonth && 
                                   span.startDay === span.startDate.getDate();
              const shouldStartAtCenter = isActualStart && !span.connectsToPrevious;
              const leftPercent = shouldStartAtCenter
                ? (span.startCol + 0.5) * cellWidth  // Center of the cell - actual start (not connected)
                : span.startCol * cellWidth;  // Left edge of the cell - continuation or connecting to previous
              
              // Determine if this segment is ACTUALLY the end of the leave
              // Only center if this is where the leave actually ends AND it doesn't connect to a next leave
              // Otherwise, end at right edge (continues to next month/week OR connecting to next leave)
              const actualEndDateInCurrentMonth = span.endDate.getMonth() === currentMonth && 
                                                   span.endDate.getFullYear() === currentYear;
              const isActualEnd = actualEndDateInCurrentMonth && 
                                 span.endDay === span.endDate.getDate();
              const shouldEndAtCenter = isActualEnd && !span.connectsToNext;
              const rightPercent = shouldEndAtCenter
                ? 100 - ((span.endCol + 0.5) * cellWidth)  // Center of the cell - actual end (not connected)
                : 100 - ((span.endCol + 1) * cellWidth);  // Right edge of the cell - continuation or connecting to next
              
              // Calculate vertical positioning with dynamic row heights
              const baseHeight = 60;
              const gapBetweenRows = 1; // Grid gap in pixels
              const rowHeights = maxStackPerRow.map(maxStack => {
                const stackHeight = maxStack > 0 ? maxStack * 11 + 10 : 0;
                return baseHeight + stackHeight;
              });
              
              // Calculate accumulated height to this row (including gaps)
              const accumulatedHeight = rowHeights.slice(0, span.row).reduce((sum, h) => sum + h, 0) + (span.row * gapBetweenRows);
              const currentRowHeight = rowHeights[span.row];
              
              // Position at center of current row + stack offset
              const topPixels = accumulatedHeight + (currentRowHeight / 2) + (span.stackLevel * 11);
              
              // Calculate border-radius based on connection status
              // Remove rounding on edges that connect to adjacent leaves
              const borderRadius = (() => {
                const hasLeftRounding = !span.connectsToPrevious && shouldStartAtCenter;
                const hasRightRounding = !span.connectsToNext && shouldEndAtCenter;
                
                if (hasLeftRounding && hasRightRounding) {
                  return '4px'; // Normal: both sides rounded
                } else if (hasLeftRounding && !hasRightRounding) {
                  return '4px 0 0 4px'; // Left rounded, right flat (connects to next)
                } else if (!hasLeftRounding && hasRightRounding) {
                  return '0 4px 4px 0'; // Left flat (connects from previous), right rounded
                } else {
                  return '0'; // Middle segment: both sides flat
                }
              })();
              
              return (
                <div
                  key={`span-${index}`}
                  className="leave-line"
                  style={{
                    left: `${leftPercent}%`,
                    right: `${rightPercent}%`,
                    top: `${topPixels}px`,
                    backgroundColor: span.color,
                    borderRadius: borderRadius
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Custom Tooltip - Rendered via Portal */}
      {tooltipDate && (getLeaveRequestsForDate(tooltipDate).length > 0 || getHolidaysForDate(tooltipDate).length > 0) && ReactDOM.createPortal(
        <div 
          className="leave-tooltip"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
          }}
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
        >
          <div className="leave-tooltip-arrow"></div>
          <div className="leave-tooltip-content">
            {/* Public Holidays Section */}
            {getHolidaysForDate(tooltipDate).map((holiday, idx) => {
              return (
                <div key={`holiday-${idx}`} className="holiday-tooltip-item">
                  <div className="holiday-tooltip-info">
                    <div className="holiday-tooltip-country">{holiday.country}</div>
                    <div className="holiday-tooltip-name">{holiday.name}</div>
                    <span className={`holiday-badge ${holiday.type}`}>
                      {holiday.type.charAt(0).toUpperCase() + holiday.type.slice(1)}
                    </span>
                  </div>
                </div>
              );
            })}
            
            {/* Divider between holidays and leave requests */}
            {getHolidaysForDate(tooltipDate).length > 0 && getLeaveRequestsForDate(tooltipDate).length > 0 && (
              <div className="holiday-divider"></div>
            )}
            
            {/* Leave Requests Section */}
            {getLeaveRequestsForDate(tooltipDate).map((request, idx) => (
              <div key={`leave-${idx}`} className="leave-tooltip-item">
                <div 
                  className="leave-tooltip-dot" 
                  style={{ backgroundColor: request.leave_type_details?.color || '#4CAF50' }}
                ></div>
                <div className="leave-tooltip-info">
                  {/* Show employee name in team view */}
                  {currentView === 'team' && request.employee_name && (
                    <div className="leave-tooltip-employee">{request.employee_name}</div>
                  )}
                  <div className="leave-tooltip-type-row">
                    <div className="leave-tooltip-type">{request.leave_type_details?.name || 'Absent'}</div>
                    <span className="leave-tooltip-date-range">
                      {formatDateRange(request.start_date, request.end_date)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default LeaveCalendarWidget;

